import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import type { Profile, CreditWallet, Subscription } from "@/lib/database.types";

interface AuthState {
  session: Session | null;
  profile: Profile | null;
  wallet: CreditWallet | null;
  subscription: Subscription | null;
  loading: boolean;
  isAdmin: boolean;
  hasActiveSub: boolean;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [wallet, setWallet] = useState<CreditWallet | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);

  const loadUserData = useCallback(async (uid: string) => {
    const [{ data: p }, { data: w }, { data: s }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("credit_wallets").select("*").eq("user_id", uid).maybeSingle(),
      supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", uid)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);
    setProfile(p ?? null);
    setWallet(w ?? null);
    setSubscription(s ?? null);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    if (data.session?.user) await loadUserData(data.session.user.id);
  }, [loadUserData]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session?.user) await loadUserData(data.session.user.id);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      if (event === "PASSWORD_RECOVERY") setPasswordRecovery(true);
      setSession(sess);
      if (sess?.user) {
        await loadUserData(sess.user.id);
      } else {
        setProfile(null);
        setWallet(null);
        setSubscription(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [loadUserData]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const clearPasswordRecovery = useCallback(() => setPasswordRecovery(false), []);

  const hasActiveSub =
    !!subscription &&
    subscription.status === "active" &&
    !!subscription.current_period_end &&
    new Date(subscription.current_period_end) > new Date();

  const value: AuthState = {
    session,
    profile,
    wallet,
    subscription,
    loading,
    isAdmin: profile?.role === "admin",
    hasActiveSub,
    passwordRecovery,
    clearPasswordRecovery,
    refresh,
    signOut,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
